import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { AuthThemeDirective } from 'src/app/core/directives/auth-theme-directive';
import { ConfettiComponent } from 'src/app/shared/components/confetti/confetti.component';
import { FormPasswordComponent } from 'src/app/shared/components';
import { AuthMethod } from 'src/app/core/data-models/auth-method.model';
import { AuthMode } from 'src/app/core/data-models/auth-mode.model';
import { BRAND_ASSETS } from 'src/app/core/constants/brand-assets';

/**
 * Sets the new password once the reset code has been verified.
*/
@Component({
  selector: 'app-reset-password',
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, AuthThemeDirective, ConfettiComponent, FormPasswordComponent],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;
  resetForm: FormGroup;
  loading = false;
  errorMessage = '';
  oobCode = '';
  userEmail = '';
  success = false;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.resetForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator },
    );

    // currentNavigation() is only populated during the navigation itself, so it
    // has to be read here; history.state covers back/forward.
    const state = this.router.currentNavigation()?.extras.state ?? history.state;
    if (!state?.oobCode || !state?.email) {
      this.router.navigate(['/forgot-password']);
      return;
    }
    this.oobCode = state.oobCode;
    this.userEmail = state.email;
  }

  async onSubmit(): Promise<void> {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const { password } = this.resetForm.value;
      await this.authService.confirmPasswordReset(this.oobCode, password);
      this.success = true;
      this.router.navigate(['/success'], { state: {mode: AuthMode.reset, method: AuthMethod.email }})
    } catch (error: any) {
      this.errorMessage = error.message;
    } finally {
      this.loading = false;
    }
  }

  goBackToLogin(): void {
    this.router.navigate(['/login']);
  }

  // Password must contain upper, lower, digit, and special character
  private passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumeric = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return (hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar)
      ? null
      : { weakPassword: true };
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password');
    const confirmPassword = group.get('confirmPassword');
    if (!password || !confirmPassword) return null;
    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  getErrorMessage(controlName: string): string {
    const control = this.resetForm.get(controlName);
    if (!control || !control.touched) return '';

    if (control.hasError('required')) {
      return controlName === 'password' ? 'Password is required' : 'Please confirm your password';
    }
    if (control.hasError('minlength')) {
      return `Password must be at least ${control.getError('minlength').requiredLength} characters`;
    }
    if (control.hasError('weakPassword')) {
      return 'Password must contain uppercase, lowercase, number, and special character';
    }
    // `passwordMismatch` is a group-level error, so confirmPassword itself can
    // be valid while the pair still disagrees — checked after the control's own.
    if (controlName === 'confirmPassword' && this.resetForm.hasError('passwordMismatch')) {
      return 'Passwords do not match';
    }
    return '';
  }
}
